import { useDocumentCommentsPanel } from "@/domains/documents/comments/documentCommentsPanelContext";
import { useDocumentFiltersOptional } from "@/domains/documents/context/DocumentFiltersContext";
import { useDocumentVerification } from "@/domains/documents/hooks/useDocumentVerification";
import { useDocumentCommentCountsQuery } from "@/stores/documents/comments/queries";
import { useMemo } from "react";

import type { documentTableOptions } from "./documentTableOptions";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  "buildingId" | "documents" | "isRestore"
>;
export function useTableComments(options: Options) {
  const { buildingId, documents, isRestore } = options;
  // Sort and tag state are READ from the shared filters; this table owns no
  // writer for the search query. It used to carry its own header search box
  // debouncing into the context, which made two writers for one field — and
  // because the box was suppressed wherever a provider actually exists, the
  // only thing left running was its timer, re-committing the same value every
  // 300ms and re-rendering every consumer for nothing. The page-level
  // DocumentSearchInput is the single writer now.
  //
  // Optional hook so DocumentListTable works outside DocumentFiltersProvider
  // (e.g. projects page, share page, trash view, child documents dialog).
  const docFiltersContext = useDocumentFiltersOptional();
  const activeSearchQuery = docFiltersContext?.filters.searchQuery ?? "";

  // Verified mark. The hook resolves permissions and owns the write + Undo
  // toast, so the List, Folders and container views all behave identically.
  const { canVerifyDocument, setVerified } = useDocumentVerification();
  // Comments: the page mounts the panel provider; a view rendered outside one
  // (the share page, the container dialog) simply offers no Comments action.
  const commentsPanel = useDocumentCommentsPanel();
  const commentBuildingId = buildingId ? Number(buildingId) : null;
  const commentDocIds = useMemo(() => (documents ?? []).map((d) => Number(d.id)), [documents]);
  const commentCounts = useDocumentCommentCountsQuery(
    commentBuildingId,
    commentDocIds,
    Boolean(commentsPanel) && !isRestore
  );
  const openComments = (doc: { id: string; filename: string }) => {
    if (!commentsPanel || commentBuildingId == null) return;
    commentsPanel.open({
      documentId: Number(doc.id),
      buildingId: commentBuildingId,
      filename: doc.filename,
    });
  };
  const canOpenComments = Boolean(commentsPanel) && commentBuildingId != null && !isRestore;
  return {
    docFiltersContext,
    canVerifyDocument,
    setVerified,
    canOpenComments,
    openComments,
    activeSearchQuery,
    commentCounts,
  };
}
